/**
 * Read canonical or legacy TradeStation field names from stream/order/position objects.
 */

const FILLED_STATUSES = new Set(['filled', 'partially_filled', 'fll', 'filled', 'flp']);
const WORKING_STATUSES = new Set(['working', 'pending', 'opn', 'ack', 'don', 'out', 'pla', 'ucn']);
const CANCELLED_STATUSES = new Set(['cancelled', 'can', 'tsc']);
const REJECTED_STATUSES = new Set(['rejected', 'rej', 'bro']);

function normStatus(status) {
  return status == null ? '' : String(status).toLowerCase();
}

export function orderId(order) {
  return order?.order_id ?? order?.OrderID;
}

export function positionId(pos) {
  return pos?.broker_position_id ?? pos?.position_id ?? pos?.PositionID;
}

export function accountId(obj) {
  return obj?.account_id ?? obj?.AccountID;
}

/** Broker-visible account number (TS AccountID / CT account_number). */
export function accountNumber(account) {
  return account?.account_number ?? account?.AccountID;
}

export function accountType(account) {
  const t = account?.account_type ?? account?.AccountType;
  if (!t) return t;
  const lower = String(t).toLowerCase();
  const map = { cash: 'Cash', margin: 'Margin', futures: 'Futures', dvp: 'DVP' };
  return map[lower] || t;
}

/** Display label: CT account_name / TS Alias when present. */
export function accountName(account) {
  const raw = account?.account_name ?? account?.Alias ?? account?.AccountName;
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  return trimmed || null;
}

export function accountTypeCanonical(account) {
  const t = account?.account_type ?? account?.AccountType;
  if (!t) return null;
  return String(t).toLowerCase();
}

export function accountIsPaperTrading(account) {
  if (account?.is_paper_trading != null) return account.is_paper_trading;
  if (account?.isPaperTrading != null) return account.isPaperTrading;
  const num = accountNumber(account);
  return num ? String(num).toUpperCase().startsWith('SIM') : false;
}

export function connectionId(account) {
  return account?.connection_id ?? account?.ct_connection_id;
}

export function accountsArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.accounts)) return payload.accounts;
  if (Array.isArray(payload.Accounts)) return payload.Accounts;
  return [];
}

export function balancesArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.balances)) return payload.balances;
  if (Array.isArray(payload.Balances)) return payload.Balances;
  if (payload.account_id || payload.AccountID) return [payload];
  return [];
}

export function balanceAccountType(balance) {
  const t = balance?.account_type ?? balance?.AccountType;
  if (!t) return t;
  const lower = String(t).toLowerCase();
  const map = { cash: 'Cash', margin: 'Margin', futures: 'Futures', dvp: 'DVP' };
  return map[lower] || t;
}

export function balanceTodaysPl(balance) {
  return balance?.today_pl ?? balance?.TodaysProfitLoss;
}

export function balanceEquity(balance) {
  return balance?.equity ?? balance?.Equity;
}

export function balanceCash(balance) {
  return balance?.cash ?? balance?.CashBalance;
}

export function balanceBuyingPower(balance) {
  return balance?.buying_power ?? balance?.BuyingPower;
}

export function balanceRealizedPl(balance) {
  return (
    balance?.realized_pl ??
    balance?.origin_data?.balance_detail?.realized_profit_loss ??
    balance?.BalanceDetail?.RealizedProfitLoss
  );
}

export function balanceUnrealizedPl(balance) {
  return (
    balance?.unrealized_pl ??
    balance?.origin_data?.balance_detail?.unrealized_profit_loss ??
    balance?.BalanceDetail?.UnrealizedProfitLoss
  );
}

export function balanceAccountNumber(balance) {
  return balance?.account_number ?? balance?.AccountID;
}

export function balanceMatchesAccount(balance, account) {
  if (!balance || !account) return false;
  const balNum = balanceAccountNumber(balance);
  const acctNum = accountNumber(account);
  if (balNum && acctNum && balNum === acctNum) return true;
  const balId = accountId(balance);
  const acctId = accountId(account);
  return !!(balId && acctId && balId === acctId);
}

export function accountsMatch(a, b) {
  if (!a || !b) return false;
  const aNum = accountNumber(a);
  const bNum = accountNumber(b);
  if (aNum && bNum && aNum === bNum) return true;
  const aId = accountId(a);
  const bId = accountId(b);
  return !!(aId && bId && aId === bId);
}

export function symbol(obj) {
  return obj?.symbol ?? obj?.Symbol ?? obj?.legs?.[0]?.symbol ?? obj?.Legs?.[0]?.Symbol;
}

export function orderStatus(order) {
  return order?.normalized_status ?? order?.Status;
}

export function isOrderFilled(order) {
  return FILLED_STATUSES.has(normStatus(orderStatus(order)));
}

export function isOrderWorking(order) {
  return WORKING_STATUSES.has(normStatus(orderStatus(order)));
}

export function isOrderCancelled(order) {
  return CANCELLED_STATUSES.has(normStatus(orderStatus(order)));
}

export function isOrderRejected(order) {
  return REJECTED_STATUSES.has(normStatus(orderStatus(order)));
}

export function orderLegs(order) {
  if (Array.isArray(order?.origin_data?.legs)) return order.origin_data.legs;
  if (Array.isArray(order?.Legs)) return order.Legs;
  return [];
}

export function orderFirstLeg(order) {
  return orderLegs(order)[0] || {};
}

export function orderDisplaySymbol(order) {
  return symbol(order) || orderFirstLeg(order)?.symbol || orderFirstLeg(order)?.Symbol;
}

export function orderSide(order) {
  return (
    order?.side ??
    order?.TradeAction ??
    orderFirstLeg(order)?.buy_or_sell ??
    orderFirstLeg(order)?.BuyOrSell
  );
}

export function orderBrokerStatus(order) {
  return order?.broker_status ?? order?.Status;
}

export function orderTif(order) {
  return order?.order_tif ?? order?.Duration;
}

export function orderFilledPrice(order) {
  if (order?.filled_price != null) return order.filled_price;
  if (order?.FilledPrice != null) return order.FilledPrice;
  const leg = orderFirstLeg(order);
  return leg?.execution_price ?? leg?.ExecutionPrice ?? leg?.exec_price ?? leg?.ExecPrice;
}

export function orderCommission(order) {
  return order?.commission_fee ?? order?.CommissionFee;
}

export function orderLegQtyOrdered(order) {
  const leg = orderFirstLeg(order);
  return order?.order_qty ?? leg?.quantity_ordered ?? leg?.QuantityOrdered ?? order?.Quantity;
}

export function orderLegQtyFilled(order) {
  const leg = orderFirstLeg(order);
  const candidates = [
    order?.filled_qty,
    leg?.exec_quantity,
    leg?.ExecQuantity,
    order?.FilledQuantity,
  ];
  for (const c of candidates) {
    const n = parseFloat(c);
    if (Number.isFinite(n) && n > 0) return c;
  }
  return order?.FilledQuantity ?? leg?.ExecQuantity ?? null;
}

export function orderQty(order) {
  return order?.order_qty ?? order?.Quantity;
}

export function filledQty(order) {
  const leg = orderFirstLeg(order);
  const fills = order?.fills;
  let fillsSum = null;
  if (Array.isArray(fills) && fills.length > 0) {
    let total = 0;
    let saw = false;
    for (const f of fills) {
      const n = parseFloat(f?.quantity ?? f?.Quantity);
      if (Number.isFinite(n) && n > 0) {
        total += n;
        saw = true;
      }
    }
    if (saw) fillsSum = total;
  }
  const candidates = [
    order?.filled_qty,
    order?.FilledQuantity,
    leg?.ExecQuantity ?? leg?.exec_quantity,
    fillsSum,
    order?.order_qty,
    order?.Quantity,
    leg?.QuantityOrdered ?? leg?.quantity_ordered,
  ];
  for (const c of candidates) {
    const n = parseFloat(c);
    if (Number.isFinite(n) && n > 0) return c;
  }
  return null;
}

export function orderType(order) {
  const t = order?.order_type ?? order?.OrderType;
  if (!t) return t;
  const map = { Market: 'MKT', Limit: 'LMT', StopMarket: 'STP', StopLimit: 'STPLMT' };
  return map[t] || t;
}

export function limitPrice(order) {
  return order?.order_limit_price ?? order?.LimitPrice;
}

export function stopPrice(order) {
  return order?.stop_price ?? order?.StopPrice;
}

export function fillPrice(order) {
  const fills = order?.fills;
  if (Array.isArray(fills) && fills[0]?.price != null) return fills[0].price;
  const leg0 = order?.Legs?.[0];
  return (
    leg0?.ExecutionPrice ??
    leg0?.ExecPrice ??
    order?.FilledPrice ??
    limitPrice(order) ??
    stopPrice(order)
  );
}

export function openedAt(order) {
  return order?.opened_at ?? order?.OpenedDateTime;
}

export function closedAt(order) {
  return order?.closed_at ?? order?.ClosedDateTime;
}

export function brokerMessage(order) {
  return order?.broker_message ?? order?.RejectReason ?? order?.StatusDescription;
}

export function positionLastPrice(pos) {
  return pos?.last_price ?? pos?.Last ?? pos?.MarkToMarketPrice;
}

export function positionInstrument(pos) {
  return pos?.instrument ?? pos?.AssetType;
}

export function positionMarketValue(pos) {
  return pos?.market_value ?? pos?.MarketValue;
}

export function positionUnrealizedPlPercent(pos) {
  return pos?.unrealized_pl_percent ?? pos?.UnrealizedProfitLossPercent;
}

export function positionQuantity(pos) {
  const q = pos?.quantity ?? pos?.Quantity;
  return q != null ? parseFloat(q) : q;
}

export function positionAvgPrice(pos) {
  return pos?.position_average_price ?? pos?.average_price ?? pos?.AveragePrice;
}

export function positionUnrealizedPl(pos) {
  return pos?.unrealized_pl ?? pos?.UnrealizedProfitLoss;
}

export function positionDailyPl(pos) {
  return pos?.daily_pl ?? pos?.TodaysProfitLoss;
}

export function positionLongShort(pos) {
  const ls = pos?.long_short ?? pos?.LongShort;
  if (!ls) return ls;
  const lower = String(ls).toLowerCase();
  if (lower === 'long') return 'Long';
  if (lower === 'short') return 'Short';
  return ls;
}

export function positionAccountNumber(pos) {
  return pos?.account_number ?? pos?.AccountID;
}

export function positionDeleted(pos) {
  return pos?.deleted === true || pos?.Deleted === true;
}

/** Position open time (TS Timestamp / CT system_time, plus common aliases). */
export function positionOpenedAt(pos) {
  return (
    pos?.Timestamp ??
    pos?.timestamp ??
    pos?.OpenDate ??
    pos?.DateOpened ??
    pos?.OpenedDateTime ??
    pos?.opened_at ??
    pos?.system_time ??
    null
  );
}

export function positionOpenedMs(pos) {
  const raw = positionOpenedAt(pos);
  if (raw == null || raw === '') return 0;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

export function ordersArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.orders)) return payload.orders;
  if (Array.isArray(payload.Orders)) return payload.Orders;
  if (orderId(payload)) return [payload];
  return [];
}

export function positionsArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.positions)) return payload.positions;
  if (Array.isArray(payload.Positions)) return payload.Positions;
  if (positionId(payload) || (symbol(payload) && accountId(payload))) return [payload];
  return [];
}

export function isStopOrderType(order) {
  const t = orderType(order);
  return t === 'STP' || t === 'STPLMT' || t === 'StopMarket' || t === 'StopLimit';
}
